import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { useBreakpoint } from '../../hooks/useIsMobile';
import { BREAKPOINTS, CHART_COLORS } from '../../constants';
import {
  aggregateByBucket,
  aggregateByProject,
  computeTotals,
  formatCompact,
  formatCount,
  formatMoney,
  formatPercent,
  relativeChange,
} from './stat.utils';
import { MetricCard } from './MetricCard';
import './charts.css';

const TEXT_COLOR = 'var(--color-fg)';
const GRID_COLOR = 'var(--color-line)';
const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-surface)',
  border: `1px solid ${GRID_COLOR}`,
  borderRadius: '8px',
  fontSize: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
};

const compact = (value) => formatCompact(value);
const money = (value) => formatMoney(value);

const ChartCard = ({ title, hint, children }) => (
  <div className="h-60 w-full min-w-0 overflow-hidden rounded-lg border border-card-line border-t-card-line-top bg-card shadow-soft transition-shadow hover:shadow-medium md:h-[300px] lg:w-[calc(33.33%-16px)] lg:min-w-[320px]">
    <div
      className="border-b border-line bg-white/[0.02] px-3.5 py-2.5 text-[0.85rem] font-semibold tracking-[0.01em] text-fg md:px-5 md:py-4 md:text-base"
      title={hint}
    >
      {title}
    </div>
    <div className="h-[calc(100%-42px)] p-1.5 md:h-[calc(100%-54px)] md:p-4">{children}</div>
  </div>
);

export function StatCharts({ stat, previousStat, filters, onSelectProject }) {
  const { timezone, interval, compare } = filters;
  const isMobileView = useBreakpoint(BREAKPOINTS.MOBILE);

  // Bucketed in the SELECTED timezone at the SELECTED interval, so the charts
  // agree with the grid. Aggregation lives in stat.utils so the same helpers
  // can be reused for period-over-period comparison.
  const chartData = useMemo(
    () => aggregateByBucket(stat, timezone, interval),
    [stat, timezone, interval],
  );
  const projectData = useMemo(() => aggregateByProject(stat), [stat]);
  const metrics = useMemo(() => computeTotals(stat), [stat]);

  // Same-length window immediately before the selected one. null unless the
  // user turned comparison on, so the cards simply omit the delta.
  const previous = useMemo(
    () => (compare && previousStat ? computeTotals(previousStat) : null),
    [compare, previousStat],
  );
  const delta = (key) => (previous ? relativeChange(metrics[key], previous[key]) : null);

  // Drill down into a project. Because filters live in the URL, the resulting
  // view is shareable and the back button undoes it.
  const handleBarClick = (payload) => {
    const name = payload?.name ?? payload?.payload?.name;
    if (name) onSelectProject?.(name);
  };

  if (!chartData.length) {
    return (
      <div className="rounded-lg border border-card-line bg-card px-6 py-10 text-center">
        <p className="text-sm text-fg-muted">No data for the selected period</p>
        <p className="mt-1 text-[0.8rem] text-fg-subtle">
          Try widening the date range, or switching project to “All Projects”.
        </p>
      </div>
    );
  }

  const axisProps = {
    tick: { fill: TEXT_COLOR, fontSize: 11 },
    tickLine: false,
  };

  const xAxisProps = {
    ...axisProps,
    dataKey: 'label',
    axisLine: { stroke: GRID_COLOR },
    interval: 'preserveStartEnd',
    minTickGap: isMobileView ? 24 : 16,
    dy: 10,
  };

  return (
    <div className="mb-4 animate-fade-in md:mb-6">
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] md:gap-5">
        <MetricCard
          title="Total Clicks"
          value={formatCount(metrics.clicks)}
          color={CHART_COLORS[0]}
          delta={delta('clicks')}
          previousLabel={previous ? formatCount(previous.clicks) : null}
        />
        <MetricCard
          title="Total Installs"
          value={formatCount(metrics.installs)}
          color={CHART_COLORS[1]}
          delta={delta('installs')}
          previousLabel={previous ? formatCount(previous.installs) : null}
        />
        <MetricCard
          title="Install CR"
          value={formatPercent(metrics.installCr)}
          color={CHART_COLORS[2]}
          delta={delta('installCr')}
          previousLabel={previous ? formatPercent(previous.installCr) : null}
        />
        <MetricCard
          title="Total Revenue"
          value={money(metrics.revenue)}
          color={CHART_COLORS[3]}
          delta={delta('revenue')}
          previousLabel={previous ? money(previous.revenue) : null}
        />
      </div>

      <div className="mt-4 flex flex-col flex-wrap justify-between gap-3 md:mt-6 lg:flex-row lg:gap-6">
        <ChartCard title="Clicks & Installs">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis {...axisProps} axisLine={false} tickFormatter={compact} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => [Number(value).toLocaleString(), name]}
              />
              <Legend wrapperStyle={{ paddingTop: '15px', fontSize: '12px', color: TEXT_COLOR }} />
              <Line
                type="monotone"
                dataKey="clicks"
                stroke={CHART_COLORS[0]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 0 }}
                name="Clicks"
              />
              <Line
                type="monotone"
                dataKey="installs"
                stroke={CHART_COLORS[1]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 0 }}
                name="Installs"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue Trend">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS[3]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLORS[3]} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis {...xAxisProps} />
              <YAxis
                {...axisProps}
                axisLine={false}
                tickFormatter={(value) => `$${compact(value)}`}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => [money(value), 'Revenue']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke={CHART_COLORS[3]}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                strokeWidth={2}
                name="Revenue"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Project Performance" hint="Click a bar to drill into that project">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={projectData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis {...axisProps} dataKey="name" axisLine={{ stroke: GRID_COLOR }} dy={10} />
              {/* Installs and revenue differ by orders of magnitude — a shared
                  axis flattens one of them into the baseline. */}
              <YAxis yAxisId="installs" {...axisProps} axisLine={false} tickFormatter={compact} />
              <YAxis
                yAxisId="revenue"
                orientation="right"
                {...axisProps}
                axisLine={false}
                tickFormatter={(value) => `$${compact(value)}`}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                // The cursor highlight renders above the bars; without this it
                // swallows clicks meant for them.
                cursor={{ fill: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }}
                formatter={(value, name) =>
                  name === 'Revenue' ? [money(value), name] : [Number(value).toLocaleString(), name]
                }
              />
              <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '12px', color: TEXT_COLOR }} />
              <Bar
                yAxisId="installs"
                dataKey="installs"
                fill={CHART_COLORS[1]}
                name="Installs"
                radius={[4, 4, 0, 0]}
                cursor={onSelectProject ? 'pointer' : undefined}
                onClick={handleBarClick}
              />
              <Bar
                yAxisId="revenue"
                dataKey="revenue"
                fill={CHART_COLORS[0]}
                name="Revenue"
                radius={[4, 4, 0, 0]}
                cursor={onSelectProject ? 'pointer' : undefined}
                onClick={handleBarClick}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
